/**
 * CxL3 — generator sets.
 *
 * Startup and pre-functional. The set runs, its protections are proved by
 * simulation, and its signals reach the EPMS/BMS. What is **not** here:
 *
 *  - **Load bank testing and step loading** — CxL4. It needs a bank, a schedule
 *    and usually a separate witness, and putting it in a startup script is how a
 *    site ends up "failing" a test nobody had the equipment to run.
 *  - **Synchronising and load sharing** — CxL4/L5, and only meaningful once two
 *    sets and the controls between them are both complete.
 *
 * Draft defaults. Replace with the CxA's issued generator script when it arrives.
 */

import type { CatalogueTest } from '../catalogue.js';

export const CXL3_GENERATOR: readonly CatalogueTest[] = [
  {
    code: 'GEN-VIS',
    name: 'Visual inspection and readiness',
    cxLevel: 'CxL3',
    equipmentTypes: ['Generator'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Visual Inspection',
    checks: [
      { label: 'Rating plate matches the approved schedule', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Oil, coolant and fuel levels within operating band', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Starting batteries connected, terminals clean and tight', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Exhaust run complete, lagged and clear of combustibles', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Louvres, radiator and air path free of obstruction', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Anti-vibration mounts fitted, shipping restraints removed', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Earthing and neutral arrangement as designed and labelled', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Fire detection and suppression in the generator room complete', responseType: 'yes_no_applicable', toleranceMode: 'none' },
    ],
    shotCodes: ['GEN-VIS-PLATE', 'GEN-VIS-LEVELS', 'GEN-VIS-EARTH'],
  },
  {
    code: 'GEN-START',
    name: 'Starting and stopping',
    cxLevel: 'CxL3',
    equipmentTypes: ['Generator'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Functional Test',
    checks: [
      { label: 'Local manual start', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Set cranks and fires; no alarms on the controller' },
      { label: 'Time to reach rated speed and voltage', responseType: 'measured_pass_fail', unit: 's', tolerance: 15, toleranceMode: 'max', acceptanceCriteria: '≤ 15 s unless the spec states otherwise' },
      { label: 'Remote start signal from ATS / BMS', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Set starts on the remote signal alone' },
      { label: 'Controlled stop with cool-down run', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Set runs the cool-down period, then stops' },
      { label: 'Emergency stop, local', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Immediate shutdown, alarm latched, restart blocked until reset' },
      { label: 'Emergency stop, remote / break-glass', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Immediate shutdown from the remote point' },
      { label: 'Controller in AUTO on leaving site', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
    ],
    shotCodes: ['GEN-CTRL-AUTO', 'GEN-ESTOP'],
  },
  {
    code: 'GEN-RUN',
    name: 'No-load running parameters',
    cxLevel: 'CxL3',
    equipmentTypes: ['Generator'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Voltage Test',
    checks: [
      { label: 'Output L1-L2', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Output L2-L3', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Output L3-L1', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Frequency', responseType: 'measured_pass_fail', unit: 'Hz', nominal: 50, tolerance: 1, toleranceMode: 'percent' },
      { label: 'Phase rotation at the generator terminals', responseType: 'pass_fail_na', toleranceMode: 'none', acceptanceCriteria: 'Matches the mains supply rotation', requiresPhoto: true },
      { label: 'Voltage stable over a 10-minute run — no hunting', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Oil pressure at running speed', responseType: 'measured_pass_fail', unit: 'bar', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Coolant temperature stabilised', responseType: 'measured_pass_fail', unit: '°C', toleranceMode: 'none' },
    ],
    shotCodes: ['GEN-RUN-METER', 'GEN-RUN-CTRL', 'GEN-PR-METER'],
  },
  {
    code: 'GEN-PROT',
    name: 'Protection and shutdown simulations',
    cxLevel: 'CxL3',
    equipmentTypes: ['Generator'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Functional Test',
    note: 'Simulated at the sensor or by the controller test function. Record which method was used.',
    checks: [
      { label: 'Low oil pressure shutdown', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Set shuts down, alarm latched and annunciated' },
      { label: 'High coolant temperature shutdown', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Set shuts down, alarm latched and annunciated' },
      { label: 'Overspeed shutdown', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Set shuts down at the controller setpoint' },
      { label: 'Fail to start / overcrank', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Crank cycles per the controller setting, then a latched alarm' },
      { label: 'Generator circuit breaker trip', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Breaker opens and indicates TRIP locally and at EPMS/BMS' },
      { label: 'Alarm reset restores the set to AUTO', responseType: 'pass_fail_na', toleranceMode: 'none' },
    ],
    shotCodes: ['GEN-PROT-ALARM', 'GEN-PROT-SHUTDOWN'],
  },
  {
    code: 'GEN-FUEL',
    name: 'Fuel system',
    cxLevel: 'CxL3',
    equipmentTypes: ['Generator'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Functional Test',
    checks: [
      { label: 'Day tank level indication reads correctly', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Transfer pump auto start at low level', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Pump runs and fills to the high-level setpoint' },
      { label: 'Transfer pump auto stop at high level', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Pump stops; no overfill' },
      { label: 'High-high level alarm and overflow route proved', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Alarm raised; overflow returns to the bulk tank' },
      { label: 'Fuel lines, bund and pipework free of leaks', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Fuel solenoid closes on shutdown', responseType: 'pass_fail_na', toleranceMode: 'none' },
    ],
    shotCodes: ['GEN-FUEL-LEVEL', 'GEN-FUEL-PUMP'],
  },
  {
    code: 'GEN-BATT',
    name: 'Starting battery and charger',
    cxLevel: 'CxL3',
    equipmentTypes: ['Generator'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Functional Test',
    checks: [
      { label: 'Battery terminal voltage at rest', responseType: 'measured_pass_fail', unit: 'V', nominal: 24, tolerance: 10, toleranceMode: 'percent' },
      { label: 'Charger output in float', responseType: 'measured_pass_fail', unit: 'V', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Charger fail alarm', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Alarm at controller and EPMS/BMS' },
      { label: 'Battery low-volt alarm', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Alarm at controller and EPMS/BMS' },
      { label: 'Jacket water heater energised and thermostat operating', responseType: 'yes_no_applicable', toleranceMode: 'none' },
    ],
    shotCodes: ['GEN-BATT-METER', 'GEN-CHG-DISPLAY'],
  },
  {
    code: 'GEN-ALARM',
    name: 'EPMS / BMS alarm points',
    cxLevel: 'CxL3',
    equipmentTypes: ['Generator'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Functional Check with EPMS/BMS',
    checks: [
      { label: 'Running / stopped status', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'AUTO / MANUAL mode point', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Common alarm point', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Fuel level value', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Generator breaker status', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Battery / charger fail point', responseType: 'pass_fail_na', toleranceMode: 'none' },
    ],
    shotCodes: ['GEN-BMS-SCREEN'],
  },
];
